import { MenuOutlined } from '@ant-design/icons';
import { Button, Drawer, Layout, Menu, Select, Tabs } from 'antd';
import React, { useMemo, useState } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';

const { Sider, Content } = Layout;

export type SectionNavItem = {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
};

type Props = {
  items: SectionNavItem[];
  activeKey: string;
  onChange: (key: string) => void;
  children: React.ReactNode;
  /** 桌面侧栏宽度 */
  siderWidth?: number;
  /**
   * 移动端导航形态：
   * - tabs：顶部 Tabs / 下拉（默认，兼容客户/设备等页）
   * - drawer：收叠为汉堡菜单 + 左侧抽屉（类似主导航，出差模块使用）
   */
  mobileMode?: 'tabs' | 'drawer';
  /** drawer 模式下内容区内边距 */
  contentPadding?: number | string;
};

/**
 * 详情页分区导航：桌面左侧菜单；移动端 Tabs 或左侧抽屉。
 */
const SectionNavLayout: React.FC<Props> = ({
  items,
  activeKey,
  onChange,
  children,
  siderWidth = 180,
  mobileMode = 'tabs',
  contentPadding,
}) => {
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);

  const activeLabel = useMemo(() => {
    const hit = items.find((i) => i.key === activeKey);
    return hit?.label ?? '导航';
  }, [items, activeKey]);

  const menuItems = useMemo(
    () =>
      items.map((i) => ({
        key: i.key,
        icon: i.icon,
        label: i.label,
      })),
    [items],
  );

  if (isMobile && mobileMode === 'drawer') {
    return (
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            marginBottom: 12,
            padding: '4px 0',
          }}
        >
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setNavOpen(true)}
            aria-label="打开分区导航"
          />
          <span
            style={{
              fontWeight: 600,
              fontSize: 15,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {activeLabel}
          </span>
        </div>
        <div
          style={{
            background: '#fff',
            borderRadius: 8,
            border: '1px solid rgba(5,5,5,0.06)',
            padding: contentPadding ?? 12,
            minHeight: 320,
          }}
        >
          {children}
        </div>
        <Drawer
          title="出差导航"
          placement="left"
          width={Math.min(280, typeof window !== 'undefined' ? window.innerWidth * 0.78 : 280)}
          open={navOpen}
          onClose={() => setNavOpen(false)}
          styles={{ body: { padding: '8px 0' } }}
        >
          <Menu
            mode="inline"
            selectedKeys={[activeKey]}
            items={menuItems}
            onClick={({ key }) => {
              onChange(key);
              setNavOpen(false);
            }}
            style={{ borderInlineEnd: 'none' }}
          />
        </Drawer>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          border: '1px solid rgba(5,5,5,0.06)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '8px 12px 0',
            borderBottom: '1px solid rgba(5,5,5,0.06)',
            background: '#fafafa',
          }}
        >
          {items.length > 5 ? (
            <Select
              value={activeKey}
              onChange={onChange}
              options={items.map((i) => ({
                value: i.key,
                label: i.label,
              }))}
              style={{ width: '100%', marginBottom: 8 }}
              size="large"
            />
          ) : (
            <Tabs
              activeKey={activeKey}
              onChange={onChange}
              size="small"
              tabBarGutter={12}
              items={items.map((i) => ({
                key: i.key,
                label: i.label,
              }))}
              tabBarStyle={{ marginBottom: 0 }}
              className="section-nav-tabs"
              style={{ marginBottom: 0 }}
            />
          )}
        </div>
        <div style={{ padding: contentPadding ?? 12 }}>{children}</div>
      </div>
    );
  }

  return (
    <Layout
      style={{
        background: '#fff',
        borderRadius: 8,
        minHeight: 480,
        border: '1px solid rgba(5,5,5,0.06)',
        overflow: 'hidden',
      }}
    >
      <Sider
        width={siderWidth}
        theme="light"
        style={{
          background: '#fff',
          borderRight: '1px solid rgba(5,5,5,0.06)',
        }}
      >
        <Menu
          mode="inline"
          selectedKeys={[activeKey]}
          items={menuItems}
          onClick={({ key }) => onChange(key)}
          style={{
            borderInlineEnd: 'none',
            paddingTop: 8,
            background: '#fff',
          }}
        />
      </Sider>
      <Content style={{ padding: contentPadding ?? 24, background: '#fff' }}>
        {children}
      </Content>
    </Layout>
  );
};

export default SectionNavLayout;
